/**
 * Setup application wizard — assist for all steps.
 * Choice chips / aliases stay deterministic; unsure & unmatched free text → raw agent.
 */
import type { WizardQuickOption } from './vysorWizardEngine';
import {
  buildAgentContextHeader,
  isUnsureInput,
  shouldDelegateToAgent,
  DYNAMIC_AI_REPLY_RULES,
} from './vysorWizardAssistCommon';

export type SetupAppAssistField =
  | 'name'
  | 'application_id'
  | 'business_function'
  | 'description'
  | 'risk_level'
  | 'status';

export type SetupAppAssistOutcome =
  | { kind: 'pass' }
  | { kind: 'inferred'; fieldKey: SetupAppAssistField; value: string; summary: string }
  | { kind: 'agent'; message: string; attachmentText?: string }
  | { kind: 'invalid'; hint: string };

const BUSINESS_KEYWORDS: Record<string, string[]> = {
  HR: ['hr', 'human resources', 'payroll', 'people', 'talent', 'recruiting'],
  Finance: ['finance', 'accounting', 'erp', 'billing', 'invoice', 'treasury'],
  Engineering: ['engineering', 'developer', 'devops', 'gitlab', 'github', 'jira', 'ci/cd'],
  IT: ['it ', 'information technology', 'infra', 'service desk', 'helpdesk', 'monitoring'],
  Sales: ['sales', 'crm', 'salesforce', 'pipeline', 'revenue'],
  Marketing: ['marketing', 'campaign', 'hubspot', 'marketo', 'analytics'],
  Operations: ['operations', 'supply chain', 'logistics', 'warehouse'],
};

const RISK_KEYWORDS: Record<string, string[]> = {
  Low: ['low risk', 'internal only', 'no pii', 'public data', 'read-only'],
  Medium: ['medium', 'internal', 'some pii', 'employee data'],
  High: ['high', 'pii', 'pci', 'hipaa', 'regulated', 'customer data', 'production'],
  Critical: ['critical', 'sox', 'pci-dss', 'classified', 'financial reporting'],
};

const CHOICE_ALIASES: Partial<Record<SetupAppAssistField, Record<string, string>>> = {
  business_function: {
    hr: 'HR',
    humanresources: 'HR',
    finance: 'Finance',
    fin: 'Finance',
    engineering: 'Engineering',
    eng: 'Engineering',
    it: 'IT',
    informationtechnology: 'IT',
    sales: 'Sales',
    marketing: 'Marketing',
    operations: 'Operations',
    ops: 'Operations',
  },
  risk_level: {
    low: 'Low',
    medium: 'Medium',
    med: 'Medium',
    high: 'High',
    critical: 'Critical',
    crit: 'Critical',
    notdefined: 'Not Defined',
    undefined: 'Not Defined',
  },
  status: {
    active: 'Active',
    inactive: 'Inactive',
    enabled: 'Active',
    disabled: 'Inactive',
  },
};

function normalizeLookupKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

export function buildSetupAppChoiceOptions(options: string[]): WizardQuickOption[] {
  return [
    ...options.map((o, i) => ({ id: `wz_opt_${i}`, label: o, value: o })),
    { id: 'wz_cancel', label: 'Cancel', value: '__cancel__' },
  ];
}

function matchSetupChoice(fieldKey: SetupAppAssistField, choiceOptions: string[], trimmed: string): string | undefined {
  if (!choiceOptions.length || !trimmed) return undefined;
  const exact = choiceOptions.find(o => o === trimmed || o.toLowerCase() === trimmed.toLowerCase());
  if (exact) return exact;
  const norm = normalizeLookupKey(trimmed);
  const aliases = CHOICE_ALIASES[fieldKey];
  if (aliases) {
    const mapped = aliases[norm];
    if (mapped && choiceOptions.includes(mapped)) return mapped;
  }
  const normalizedMatch = choiceOptions.filter(o => normalizeLookupKey(o) === norm);
  if (normalizedMatch.length === 1) return normalizedMatch[0];
  return undefined;
}

export function inferBusinessFunctionFromName(text: string): { value: string; confidence: 'high' | 'medium' } | null {
  const lower = text.toLowerCase();
  let best: { key: string; score: number } | null = null;
  for (const [bf, keys] of Object.entries(BUSINESS_KEYWORDS)) {
    const score = keys.filter(k => lower.includes(k)).length;
    if (score > 0 && (!best || score > best.score)) best = { key: bf, score };
  }
  if (!best) return null;
  return { value: best.key, confidence: best.score >= 2 ? 'high' : 'medium' };
}

function inferBusinessFunction(text: string): { value: string; confidence: 'high' | 'medium' } | null {
  return inferBusinessFunctionFromName(text);
}

function inferRiskLevel(text: string): { value: string; confidence: 'high' | 'medium' } | null {
  const lower = text.toLowerCase();
  for (const [risk, keys] of Object.entries(RISK_KEYWORDS)) {
    if (keys.some(k => lower.includes(k))) {
      return { value: risk, confidence: 'medium' };
    }
  }
  return null;
}

export function buildSetupAppAgentContext(
  fieldKey: SetupAppAssistField,
  collected: Record<string, string>,
  userMessage: string,
  attachmentText?: string,
  choiceOptions?: string[],
): string {
  const choiceHint = choiceOptions?.length
    ? `When a concrete choice helps, recommend one of: ${choiceOptions.join(', ')}.`
    : '';
  const fieldHints: Record<SetupAppAssistField, string> = {
    name: 'Help them pick a clear display name for the catalog.',
    application_id: 'Help them pick a lowercase slug (e.g. salesforce-prod). Keep it URL-safe.',
    business_function:
      'Recommend HR, Finance, Engineering, IT, Sales, Marketing, Operations, or a custom Other value when helpful.',
    description: 'Help them write a short app description, or suggest Skip if optional.',
    risk_level: 'Recommend risk level: Not Defined, Low, Medium, High, or Critical when helpful.',
    status: 'Recommend Active or Inactive for the initial catalog status.',
  };
  const lines = [
    `User is on Setup application — field: ${fieldKey}.`,
    `App display name: ${collected.name || 'unknown'}`,
    `App slug: ${collected.application_id || 'pending'}`,
    collected.business_function ? `Business function so far: ${collected.business_function}` : '',
    collected.risk_level ? `Risk so far: ${collected.risk_level}` : '',
    '',
    DYNAMIC_AI_REPLY_RULES,
    fieldHints[fieldKey],
    choiceHint,
    '',
    `User: ${userMessage}`,
  ].filter(Boolean);
  const header = buildAgentContextHeader('Setup application wizard', lines);
  if (attachmentText?.trim()) {
    return `${header}\n[Reference material]\n${attachmentText.slice(0, 6000)}`;
  }
  return header;
}

export function resolveSetupAppAssistInput(
  fieldKey: SetupAppAssistField,
  rawAnswer: string,
  optionValue: string | undefined,
  collected: Record<string, string>,
  choiceOptions: string[] | undefined,
  assistActive: boolean,
): SetupAppAssistOutcome {
  const trimmed = rawAnswer.trim();
  const options = choiceOptions || [];

  if (optionValue && (options.includes(optionValue) || optionValue === '__skip__' || optionValue === '__cancel__')) {
    return { kind: 'pass' };
  }

  if (isUnsureInput(trimmed)) {
    return {
      kind: 'agent',
      message: buildSetupAppAgentContext(
        fieldKey,
        collected,
        trimmed || `I am not sure about ${fieldKey}.`,
        undefined,
        options,
      ),
    };
  }

  // Free-text name / slug / description — only agent when stuck; otherwise pass to engine.
  if (fieldKey === 'name' || fieldKey === 'application_id' || fieldKey === 'description') {
    if (shouldDelegateToAgent(trimmed, assistActive)) {
      return {
        kind: 'agent',
        message: buildSetupAppAgentContext(fieldKey, collected, trimmed, undefined, options),
      };
    }
    if (!trimmed && fieldKey !== 'description') {
      return { kind: 'invalid', hint: 'Enter a value, ask in your own words, or tap Cancel.' };
    }
    return { kind: 'pass' };
  }

  if (fieldKey === 'business_function') {
    const custom = trimmed.match(/^other[:\-\s]+(.+)$/i)?.[1]?.trim();
    if (custom) return { kind: 'pass' };
  }

  if (options.length && matchSetupChoice(fieldKey, options, trimmed)) {
    return { kind: 'pass' };
  }

  if (fieldKey === 'business_function') {
    const inferred = inferBusinessFunction(trimmed);
    if (inferred && trimmed.length >= 20) {
      return {
        kind: 'inferred',
        fieldKey,
        value: inferred.value,
        summary: `From your description, this looks like **${inferred.value}**.`,
      };
    }
  }

  if (fieldKey === 'risk_level') {
    const inferred = inferRiskLevel(trimmed);
    if (inferred && trimmed.length >= 15) {
      return {
        kind: 'inferred',
        fieldKey,
        value: inferred.value,
        summary: `Based on what you described, I'd suggest **${inferred.value}** risk.`,
      };
    }
  }

  if (shouldDelegateToAgent(trimmed, assistActive) || trimmed) {
    return {
      kind: 'agent',
      message: buildSetupAppAgentContext(
        fieldKey,
        collected,
        trimmed || `I am not sure about ${fieldKey}.`,
        undefined,
        options,
      ),
    };
  }

  return {
    kind: 'invalid',
    hint:
      fieldKey === 'business_function'
        ? 'Pick a chip, type **Other: Legal**, or ask in your own words.'
        : fieldKey === 'risk_level'
          ? 'Pick a risk chip, or ask in your own words.'
          : 'Pick a chip, or ask in your own words.',
  };
}
